import { COURSES, courseById } from '../data/courses.js';
import { DIDATICA } from '../data/didatica.js';
import { CAST } from '../data/cast-lines.js';
import {
  ANPD_DISCLAIMER,
  CONTENT_VERSION,
  advance,
  canStartCourse,
  createLearner,
  createStore,
  getView,
  hasPassed,
  issueCertificate,
  load,
  persist,
  recordFail,
  scoreOf,
  selectOption,
  startRetry,
  startSession,
  orderedOptions,
  submitQuiz,
  toggleMulti,
  STORAGE_KEY,
} from '../engine/index.js';
import { initSound, isMuted, play, toggleMuted, unlockAudio } from './audio.js';
import { buildCertificatePdf } from './certificatePdf.js';

const storage = window.localStorage;
const base = import.meta.env.BASE_URL || './';

function asset(path) {
  return `${base}assets/${path}`;
}

let root;
let store;
let session = null;
let course = null;
let toast = '';

function save() {
  persist(store, storage);
}

function restore() {
  const loaded = load(storage);
  store = loaded || null;
}

function setToast(msg) {
  toast = msg;
  render();
  window.setTimeout(() => {
    if (toast === msg) {
      toast = '';
      render();
    }
  }, 2800);
}

function tecaImg(pose, extra = '') {
  return `<img class="teca-sticker ${extra}" data-testid="teca" data-teca="${pose}" alt="Teca, ${pose.replace('teca-', '')}" src="${asset(`teca/${pose}.png`)}" />`;
}

function charImg(name, show) {
  if (!show) return '';
  const label = name === 'nexo' ? 'Nexo, o scout' : 'Vaza, o vazamento';
  return `<img class="sidekick ${name}" data-testid="${name}" alt="${label}" src="${asset(`characters/${name}.png`)}" />`;
}

function tip(text, label) {
  return `<span class="tip" tabindex="0" data-tip="${escapeAttr(label)}">${text}</span>`;
}

function escapeAttr(s) {
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function escapeHtml(s) {
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function iconCheck() {
  return `<span class="glyph" aria-hidden="true">✓</span>`;
}
function iconX() {
  return `<span class="glyph" aria-hidden="true">✕</span>`;
}

function lessonOf(slide) {
  const d = (course && DIDATICA[`${course.id}:${slide.n}`]) || {};
  return {
    kicker: slide.kicker || d.kicker || course.mission,
    explain: slide.explain || d.explain || '',
    example: slide.example || d.example || '',
    why: slide.why || d.why || '',
    terms: slide.terms || d.terms || [],
    learn: slide.learn || d.learn || [],
  };
}

function renderLesson(slide) {
  if (slide.scored) return '';
  const d = lessonOf(slide);
  const chunks = [];
  if (d.explain) {
    chunks.push(
      `<section class="teach" data-testid="explain"><h3>Em português claro</h3><p>${escapeHtml(d.explain)}</p></section>`,
    );
  }
  if (d.example) {
    chunks.push(
      `<section class="example" data-testid="example"><h3>Na Padaria Estrela</h3><p>${escapeHtml(d.example)}</p></section>`,
    );
  }
  if (d.why) {
    chunks.push(
      `<section class="why" data-testid="why"><h3>Por que isso importa no seu posto</h3><p>${escapeHtml(d.why)}</p></section>`,
    );
  }
  if (d.learn.length) {
    chunks.push(
      `<ul class="learn" data-testid="learn">${d.learn.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`,
    );
  }
  if (d.terms.length) {
    chunks.push(
      `<ul class="glossary" data-testid="glossary">${d.terms
        .map(
          (t) =>
            `<li><button type="button" class="tip" data-testid="tip-${escapeAttr(t.word)}" data-tip="${escapeAttr(t.tip)}">${escapeHtml(t.word)}</button></li>`,
        )
        .join('')}</ul>`,
    );
  }
  return chunks.length ? `<div class="lesson-grid">${chunks.join('')}</div>` : '';
}

function renderScreen(slide) {
  const s = slide.screen || { type: 'callout' };
  switch (s.type) {
    case 'hero': {
      const art =
        course.theme === 'cofre'
          ? 'cofre.svg'
          : course.theme === 'nebula'
            ? 'nebula.svg'
            : 'escudo.svg';
      return `<div class="hero-panel">
        <img class="drawing" src="${asset(`drawings/${art}`)}" alt="" />
        <p class="kicker">${escapeHtml(s.kicker || '')}</p>
        <h2>${escapeHtml(s.heading || slide.title)}</h2>
        <span class="badge">${escapeHtml(s.badge || '')}</span>
      </div>`;
    }
    case 'cards':
      return `<div class="grid-cards">${s.items
        .map(
          (c) => `<article class="card ${c.struck ? 'struck' : ''}">
            <h3>${escapeHtml(c.title)}</h3>
            <p>${escapeHtml(c.body || '')}</p>
          </article>`,
        )
        .join('')}${s.stamp ? `<span class="stamp">${escapeHtml(s.stamp)}</span>` : ''}</div>`;
    case 'compare':
      return `<div class="compare">
        <article class="card yes"><h3>${iconCheck()} ${escapeHtml(s.left.title)}</h3><p>${escapeHtml(s.left.body)}</p></article>
        <article class="card no"><h3>${iconX()} ${escapeHtml(s.right.title)}</h3><p>${escapeHtml(s.right.body)}</p></article>
      </div>`;
    case 'timeline':
      return `<ol class="timeline">${s.steps.map((step) => `<li>${escapeHtml(step)}</li>`).join('')}</ol>`;
    case 'roles':
      return `<div class="grid-cards">${s.items
        .map(
          (c) => `<article class="card">
            <h3>${tip(c.title, c.tip || c.title)}</h3>
            <p>${escapeHtml(c.body)}</p>
          </article>`,
        )
        .join('')}</div>`;
    case 'stones':
      return `<ul class="stones">${s.items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`;
    case 'checklist':
      return `<ul class="checks">${s.items.map((i) => `<li>${iconCheck()} ${escapeHtml(i)}</li>`).join('')}</ul>`;
    case 'pills':
      return `<ul class="pills">${s.items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`;
    case 'locks':
      return `<div class="locks"><div class="lock-main">${escapeHtml(s.main)}</div><p class="muted">${s.around} outras chaves ao redor. Consentimento é uma.</p></div>`;
    case 'list':
      return `<ul class="plain">${s.items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`;
    case 'dont':
      return `<ul class="dont">${s.items.map((i) => `<li>${iconX()} ${escapeHtml(i)}</li>`).join('')}</ul>`;
    case 'callout':
      return `<blockquote class="callout">${escapeHtml(s.body)}</blockquote>`;
    case 'flow':
      return `<ol class="flow">${s.steps.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ol>`;
    case 'columns':
      return `<div class="compare">
        <article class="card yes"><h3>${escapeHtml(s.left.title)}</h3><p>${escapeHtml(s.left.body)}</p></article>
        <article class="card"><h3>${escapeHtml(s.right.title)}</h3><p>${escapeHtml(s.right.body)}</p></article>
      </div>`;
    case 'icons':
      return `<ul class="pills">${s.items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ul>`;
    case 'steps':
      return `<ol class="steps ${s.numbered ? 'numbered' : ''}">${s.items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}</ol>`;
    case 'habits':
      return `<ul class="checks gold">${s.items.map((i) => `<li>${iconCheck()} ${escapeHtml(i)}</li>`).join('')}</ul>`;
    case 'clock':
      return `<div class="clock-face"><strong>${escapeHtml(s.heading)}</strong><p>${escapeHtml(s.body)}</p></div>`;
    case 'nebula-map':
      return `<div class="nebula-map"><span class="planet core">${escapeHtml(s.center)}</span>${s.nodes
        .map((n) => `<span class="planet">${escapeHtml(n)}</span>`)
        .join('')}</div>`;
    case 'transfer':
      return `<div class="transfer"><span>${escapeHtml(s.from)}</span><span class="arrow" aria-hidden="true">→</span><span>${escapeHtml(s.to)}</span><span class="stamp">${escapeHtml(s.seal)}</span></div>`;
    case 'certificate':
      return renderCertificate(slide);
    case 'quiz':
      return renderQuiz(slide);
    default:
      return `<p>${escapeHtml(slide.speech || '')}</p>`;
  }
}

function renderQuiz(slide) {
  const scored = slide.scored;
  const submitted = session.quizSubmitted;
  const fb = session.feedback;
  const options = orderedOptions(session, slide);
  const orderAttr = options.map((o) => o.id).join(',');
  if (scored.type === 'single') {
    return `<fieldset class="quiz" data-testid="quiz" data-option-order="${escapeAttr(orderAttr)}">
      <legend>${escapeHtml(slide.prompt || slide.speech)}</legend>
      ${options
        .map((o, i) => {
          const selected = session.pendingChoice === o.id;
          const right = submitted && o.id === scored.items[0].correct;
          const wrong = submitted && selected && !right;
          const letter = String.fromCharCode(65 + i);
          return `<label class="option ${selected ? 'selected' : ''} ${right ? 'right' : ''} ${wrong ? 'wrong' : ''}">
            <input type="radio" name="quiz" data-testid="option-${o.id}" value="${o.id}" ${selected ? 'checked' : ''} ${submitted ? 'disabled' : ''} />
            <span class="key">${letter}</span>
            <span>${escapeHtml(o.text)}</span>
            ${right ? iconCheck() : ''}
            ${wrong ? iconX() : ''}
          </label>`;
        })
        .join('')}
      ${fb ? `<div class="gabarito ${fb.correct ? 'ok' : 'bad'}" data-testid="quiz-feedback"><p data-testid="gabarito">${escapeHtml(fb.text)}</p></div>` : ''}
    </fieldset>`;
  }
  return `<fieldset class="quiz" data-testid="quiz" data-option-order="${escapeAttr(orderAttr)}">
    <legend>${escapeHtml(slide.prompt || slide.speech)}</legend>
    ${options
      .map((o, i) => {
        const selected = session.pendingMulti.includes(o.id);
        const letter = String.fromCharCode(65 + i);
        return `<label class="option ${selected ? 'selected' : ''}">
          <input type="checkbox" data-testid="option-${o.id}" value="${o.id}" ${selected ? 'checked' : ''} ${submitted ? 'disabled' : ''} />
          <span class="key">${letter}</span>
          <span>${escapeHtml(o.text)}</span>
        </label>`;
      })
      .join('')}
    ${fb ? `<div class="gabarito ${fb.correct ? 'ok' : 'bad'}" data-testid="quiz-feedback"><p data-testid="gabarito">${escapeHtml(fb.text)}</p></div>` : ''}
  </fieldset>`;
}

function renderCertificate() {
  const rec = store.records[course.id];
  const cert = rec.certificate;
  const score = scoreOf(session);
  if (!cert) {
    return `<div class="fail-panel" data-testid="fail-panel">
      <h2>Ainda não</h2>
      <p data-testid="score">${score.correct}/9</p>
      <p>A nota de corte é 7 de 9. Refaça o que errou. O próximo curso continua fechado.</p>
    </div>`;
  }
  const trilha = store.trilhaCertificate;
  const extra = trilha
    ? `<div class="trilha" data-testid="trilha-cert">
            <h3>Trilha completa</h3>
            <p>${escapeHtml(trilha.learnerName)} · ${escapeHtml(trilha.scoreLabel)}</p>
            <p class="disclaimer">${escapeHtml(trilha.disclaimer)}</p>
          </div>`
    : '';
  return `<div class="cert" data-testid="certificate">${diplomaMarkup(cert, course, extra)}</div>`;
}

function logoImg() {
  return `<img class="brand-logo" data-testid="logo-altec" alt="Altec" src="${asset('brand/altec-logo.png')}" />`;
}

function speakerSvg(on) {
  if (on) {
    return `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M4 9v6h4l5 4V5L8 9H4zm13.5 3c0-1.8-1-3.3-2.5-4v8c1.5-.7 2.5-2.2 2.5-4zM16 6.3v2.1c2 .8 3.5 2.8 3.5 5.1S18 16.8 16 17.6v2.1c3.1-.9 5.5-3.9 5.5-7.4S19.1 7.2 16 6.3z"/></svg>`;
  }
  return `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M4 9v6h4l5 4V5L8 9H4zm12.5 3L19 14.5l-1.5 1.5L15 13.5 12.5 16 11 14.5 13.5 12 11 9.5 12.5 8 15 10.5 17.5 8 19 9.5 16.5 12z"/></svg>`;
}

function headerBar() {
  const muted = isMuted();
  return `<header class="top">
    <button type="button" class="brand" data-act="hub" data-testid="brand-home">
      ${logoImg()}
      <span>Academia LGPD</span>
    </button>
    <button type="button" class="sound-toggle ${muted ? 'is-off' : 'is-on'}" data-testid="sound-toggle" data-act="sound" aria-pressed="${muted ? 'false' : 'true'}" aria-label="${muted ? 'Ligar som' : 'Desligar som'}">
      <span class="sound-icon">${speakerSvg(!muted)}</span>
      <span class="sound-switch" aria-hidden="true"><i></i></span>
      <span class="sound-label">${muted ? 'Som off' : 'Som on'}</span>
    </button>
  </header>`;
}

function scrollTop() {
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  window.scrollTo({ top: 0, left: 0, behavior: reduce ? 'auto' : 'smooth' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

function goHub() {
  play('click');
  course = null;
  session = null;
  location.hash = '#/';
  render();
  scrollTop();
}

const LESSON_POSES = [
  'teca-lousa',
  'teca-livro',
  'teca-lupa',
  'teca-abre',
  'teca-explica',
  'teca-abraco',
];

function poseFor(slide, failedCert) {
  if (failedCert) return 'teca-nao';
  if (session?.quizSubmitted && session.feedback) return session.feedback.teca;
  if (slide.beat === 'certificate') return 'teca-festeja';
  if (slide.beat === 'recap') return 'teca-agradece';
  if (slide.beat === 'lesson') return LESSON_POSES[slide.n % LESSON_POSES.length];
  return slide.teca;
}

function castOf(slide) {
  return (course && CAST[`${course.id}:${slide.n}`]) || {};
}

function nexoLine(slide, d) {
  return castOf(slide).nexo || d.nexo || 'Nexo: se identifica gente, trata com respeito.';
}

function vazaLine(slide, d) {
  return castOf(slide).vaza || d.vaza || 'Vaza: eu entro pela porta que ficou aberta.';
}

function renderCast(slide, tecaPose, d) {
  const tecaLine =
    castOf(slide).teca ||
    (slide.speech ? `Teca: ${slide.speech}` : 'Teca: vou te levando passo a passo. Sem juridiquês.');
  return `<div class="cast">
    <figure class="cast-card teca">
      ${tecaImg(tecaPose)}
      <figcaption data-testid="teca-line">${escapeHtml(tecaLine)}</figcaption>
    </figure>
    <figure class="cast-card nexo" data-testid="nexo-card">
      <img class="sidekick-static" data-testid="nexo" alt="Nexo, o scout" src="${asset('characters/nexo.png')}" />
      <figcaption data-testid="nexo-line">${escapeHtml(nexoLine(slide, d))}</figcaption>
    </figure>
    <figure class="cast-card vaza" data-testid="vaza-card">
      <img class="sidekick-static" data-testid="vaza" alt="Vaza, o vazamento" src="${asset('characters/vaza.png')}" />
      <figcaption data-testid="vaza-line">${escapeHtml(vazaLine(slide, d))}</figcaption>
    </figure>
  </div>`;
}

function diplomaMarkup(cert, courseObj, extra = '') {
  return `<article class="diploma">
    ${logoImg()}
    <p class="kicker">Academia LGPD Altec</p>
    <h2>${escapeHtml(courseObj.certName)}</h2>
    <p class="who">${escapeHtml(cert.learnerName)}</p>
    <p class="mail">${escapeHtml(cert.learnerEmail)}</p>
    <p data-testid="score">Score: ${escapeHtml(cert.scoreLabel)}</p>
    <p>Versão ${escapeHtml(cert.version)}</p>
    <p class="when">${escapeHtml(new Date(cert.issuedAt).toLocaleString('pt-BR'))}</p>
    <p class="body">${escapeHtml(courseObj.certBody)}</p>
    <p class="disclaimer" data-testid="disclaimer">${escapeHtml(ANPD_DISCLAIMER)}</p>
    ${extra}
  </article>`;
}



function renderHub() {
  const learner = store?.learner;
  return `${headerBar()}
    <main class="hub" data-testid="hub">
      <div class="hub-hero">
        ${tecaImg('teca-acena', 'hero-teca')}
        <div>
          ${logoImg()}
          <p class="kicker">Altec · obrigatório</p>
          <h1>Academia LGPD</h1>
          <p class="lede">No Brasil existe uma lei que protege informação de gente — cliente, colega, você. Chama LGPD. Este curso ensina o que fazer no seu posto, em português claro. Três missões. Certificado interno no final de cada uma.</p>
        </div>
      </div>
      <form class="id-form" data-testid="id-form" autocomplete="on">
        <div class="field">
          <label for="learner-name">Nome</label>
          <input id="learner-name" data-testid="learner-name" name="learner-name" autocomplete="name" value="${escapeAttr(learner?.name || '')}" required minlength="2" />
        </div>
        <div class="field">
          <label for="learner-email">E-mail Altec</label>
          <input id="learner-email" data-testid="learner-email" name="learner-email" type="email" autocomplete="username" inputmode="email" placeholder="voce@altec.ws" value="${escapeAttr(learner?.email || '')}" required />
        </div>
        <button type="submit" data-testid="save-learner">Guardar identidade</button>
        ${learner ? `<p class="ok-line" data-testid="id-saved">${iconCheck()} ${escapeHtml(learner.name)} · ${escapeHtml(learner.email)}</p>` : ''}
      </form>
      <section class="missions">
        ${COURSES.map(renderMission).join('')}
      </section>
    </main>`;
}

function renderMission(c) {
  const rec = store?.records[c.id];
  const locked = !store || !canStartCourse(store, c.id);
  const passed = Boolean(rec?.passed);
  return `<article class="mission ${locked ? 'locked' : ''} ${passed ? 'passed' : ''}" data-testid="mission-${c.id}" data-locked="${locked}">
    <img src="${asset(`teca/${c.theme === 'cofre' ? 'teca-cadeado' : c.theme === 'nebula' ? 'teca-nebula' : 'teca-explica'}.png`)}" alt="" />
    <div>
      <p class="kicker">${escapeHtml(c.courseLabel)} · ${escapeHtml(c.level)}</p>
      <h2>${escapeHtml(c.mission)}</h2>
      <p>${escapeHtml(c.kicker)}</p>
      ${passed ? `<p class="ok-line">${iconCheck()} certificado ${rec.score}/9</p>` : ''}
      <button type="button" class="primary" data-testid="start-${c.id}" data-start="${c.id}" ${locked ? 'disabled aria-disabled="true"' : ''}>
        ${locked ? 'Fechado' : passed ? 'Rever missão' : c.startLabel}
      </button>
      ${locked ? `<p class="lock-hint" data-testid="locked-${c.id}">Conclua o curso anterior com certificado (7/9).</p>` : ''}
    </div>
  </article>`;
}

function renderRetryIntro() {
  const missed = getView(session, course).missed;
  const labels = missed.map((id) => {
    const slide = course.slides.find((s) => s.scored?.items.some((i) => i.id === id));
    return slide ? `Tela ${slide.n} — ${slide.title}` : id;
  });
  return `${headerBar()}
    <main class="slide-page retry" data-testid="retry-intro">
      <div class="stage">
        <div class="cast">${tecaImg('teca-nao')}</div>
        <div class="board">
          <p class="kicker">Só o que faltou</p>
          <h1>Vamos de novo</h1>
          <div class="speech" data-testid="speech">
            <p>Errou ${missed.length} item(ns). Refaz só isso. Sem ranking, sem vida extra. O gabarito ensina de novo.</p>
          </div>
          <section class="teach">
            <h3>O que vamos refazer</h3>
            <ul class="learn" data-testid="retry-list">${labels.map((l) => `<li>${escapeHtml(l)}</li>`).join('')}</ul>
          </section>
          <section class="why">
            <h3>Por que só esses</h3>
            <p>Os que você acertou já valeram. A Teca não te faz repetir a missão inteira.</p>
          </section>
        </div>
      </div>
      <footer class="actions">
        <button type="button" class="primary" data-testid="btn-retry" data-act="retry">Refazer o que errei</button>
      </footer>
    </main>`;
}

function renderSlide() {
  const view = getView(session, course);
  const slide = view.slide;
  const quiz = Boolean(slide.scored);
  const submitted = session.quizSubmitted;
  const failedCert = slide.n === 30 && !store.records[course.id].certificate;
  const tecaPose = poseFor(slide, failedCert);
  const canGo = !quiz || submitted;
  const score = scoreOf(session);
  const passed = hasPassed(session);
  const nextLabel = submitted && quiz && slide.n !== 10 && slide.n !== 18 && slide.n !== 27
    ? 'Próxima pergunta'
    : slide.action;
  const reveal = quiz && !submitted;
  const emit = slide.n === 29;
  const startNextDisabled =
    slide.n === 30 && Boolean(course.nextCourseId) && !canStartCourse(store, course.nextCourseId);

  const d = lessonOf(slide);
  return `${headerBar()}
    <main class="slide-page theme-${course.theme}${quiz ? ' is-quiz' : ''}" data-testid="slide" data-slide="${slide.n}" data-beat="${slide.beat}">
      <div class="progress" data-testid="progress" aria-label="Slide ${slide.n} de 30">
        <span style="width:${(slide.n / 30) * 100}%"></span>
        <em>${slide.n} / 30 · ${escapeHtml(course.level)}</em>
      </div>
      <div class="stage">
        ${renderCast(slide, tecaPose, d)}
        <div class="board">
          <p class="kicker">${escapeHtml(d.kicker)}</p>
          <h1 data-testid="slide-title">${escapeHtml(slide.n === 30 && !store.records[course.id].certificate ? 'Ainda sem certificado' : slide.title)}</h1>
          ${slide.speech && !slide.scored && !failedCert ? `<div class="speech" data-testid="speech"><p>${escapeHtml(slide.speech)}</p></div>` : ''}
          ${slide.orientation ? `<p class="orientation">${escapeHtml(slide.orientation)}</p>` : ''}
          ${renderLesson(slide)}
          <div class="screen visual">${renderScreen(slide)}</div>
        </div>
      </div>
      <footer class="actions">
        ${reveal ? `<button type="button" class="primary" data-testid="btn-reveal" data-act="reveal" ${quiz && slide.scored.type === 'single' && !session.pendingChoice ? 'disabled' : ''}>Ver a resposta</button>` : ''}
        ${emit ? `<button type="button" class="primary" data-testid="btn-continue" data-act="emit">${passed ? 'Emitir certificado' : 'Ver resultado'}</button>` : ''}
        ${!reveal && !emit && slide.n !== 30 ? `<button type="button" class="primary" data-testid="btn-continue" data-act="continue" ${canGo ? '' : 'disabled'}>${escapeHtml(nextLabel)}</button>` : ''}
        ${slide.n === 30 ? `
          <button type="button" data-testid="btn-download" data-act="download" ${store.records[course.id].certificate ? '' : 'disabled'}>${course.id === 'rede-nebulosa' ? 'Baixar os certificados (PDF)' : 'Baixar certificado (PDF)'}</button>
          ${
            course.nextCourseId
              ? `<button type="button" class="primary" data-testid="btn-start-next" data-act="next" ${startNextDisabled ? 'disabled' : ''}>Começar o próximo curso</button>`
              : `<button type="button" class="primary" data-testid="btn-start-next" data-act="hub">Encerrar</button>`
          }
        ` : ''}
        <p class="score-live" data-testid="live-score">${score.ids?.length ? `${score.correct}/${score.total}` : ''}</p>
      </footer>
    </main>`;
}

function render() {
  if (!root) return;
  let html;
  if (!course) html = renderHub();
  else if (session && getView(session, course).kind === 'retry-intro') html = renderRetryIntro();
  else html = renderSlide();
  if (toast) html += `<div class="toast" role="status" data-testid="toast">${escapeHtml(toast)}</div>`;
  html += `<div id="tip-layer" class="tip-layer" hidden><div class="tip-balloon" data-testid="tip-balloon" role="tooltip"></div></div>`;
  root.innerHTML = html;
  bind();
}

function closeBalloon() {
  const layer = document.getElementById('tip-layer');
  if (layer) layer.hidden = true;
  root?.querySelectorAll('.tip.is-open').forEach((n) => n.classList.remove('is-open'));
}

function placeBalloon(trigger) {
  const layer = document.getElementById('tip-layer');
  const balloon = layer?.querySelector('[data-testid="tip-balloon"]');
  if (!layer || !balloon) return;
  balloon.textContent = trigger.getAttribute('data-tip') || '';
  layer.hidden = false;
  const r = trigger.getBoundingClientRect();
  const bw = Math.min(320, window.innerWidth - 32);
  balloon.style.width = `${bw}px`;
  const bh = balloon.offsetHeight;
  const spaceBelow = window.innerHeight - r.bottom;
  const preferBelow = spaceBelow > bh + 16 || r.top < bh + 24;
  let top = preferBelow ? r.bottom + 10 : r.top - bh - 10;
  let left = r.left;
  left = Math.min(Math.max(16, left), window.innerWidth - bw - 16);
  top = Math.min(Math.max(8, top), window.innerHeight - bh - 8);
  balloon.style.top = `${top}px`;
  balloon.style.left = `${left}px`;
}

function bind() {
  root.querySelectorAll('[data-act="hub"]').forEach((el) => {
    el.addEventListener('click', (ev) => {
      ev.preventDefault();
      goHub();
    });
  });
  root.querySelector('[data-act="sound"]')?.addEventListener('click', () => {
    unlockAudio();
    toggleMuted(storage);
    if (!isMuted()) play('toggle');
    render();
  });
  root.querySelector('.id-form')?.addEventListener('submit', (ev) => {
    ev.preventDefault();
    unlockAudio();
    play('click');
    const name = root.querySelector('[data-testid="learner-name"]').value;
    const email = root.querySelector('[data-testid="learner-email"]').value;
    try {
      const learner = createLearner(name, email);
      store = createStore(learner);
      const prev = load(storage);
      if (prev?.learner?.email === learner.email) store = prev;
      store.learner = learner;
      save();
      toast = '';
      render();
    } catch (err) {
      setToast(err.message === 'EMAIL_ALTEC' ? 'Use e-mail @altec.ws' : 'Preencha nome e e-mail Altec.');
    }
  });
  root.querySelectorAll('[data-start]').forEach((btn) => {
    btn.addEventListener('click', () => {
      unlockAudio();
      const id = btn.getAttribute('data-start');
      if (!store) {
        play('lock');
        setToast('Guarde nome e e-mail Altec antes.');
        return;
      }
      const next = courseById(id);
      try {
        toast = '';
        session = startSession(store, next);
        course = next;
        save();
        play('whoosh');
        location.hash = `#/curso/${id}`;
        render();
        scrollTop();
      } catch (err) {
        play('lock');
        btn.classList.add('shake');
        setToast('Conclua o curso anterior com certificado (7/9).');
      }
    });
  });
  root.querySelectorAll('.option input').forEach((input) => {
    input.addEventListener('change', () => {
      play('select');
      const slide = getView(session, course).slide;
      if (slide.scored.type === 'single') selectOption(session, input.value);
      else toggleMulti(session, input.value);
      render();
    });
  });
  root.querySelector('[data-act="reveal"]')?.addEventListener('click', () => {
    const slide = getView(session, course).slide;
    try {
      const fb = submitQuiz(session, slide);
      play(fb.correct ? 'correct' : 'wrong');
      render();
      scrollTop();
    } catch (err) {
      play('lock');
      setToast('Marque uma opção para ver a resposta.');
    }
  });
  root.querySelector('[data-act="continue"]')?.addEventListener('click', () => {
    try {
      play('whoosh');
      advance(session, course);
      render();
      scrollTop();
    } catch (err) {
      play('lock');
      setToast('Marque uma opção antes de avançar.');
    }
  });
  root.querySelector('[data-act="retry"]')?.addEventListener('click', () => {
    play('retry');
    startRetry(session, course);
    render();
    scrollTop();
  });
  root.querySelector('[data-act="emit"]')?.addEventListener('click', () => {
    if (hasPassed(session)) {
      issueCertificate(store, session, course, new Date());
      save();
      play('celebrate');
    } else {
      recordFail(store, session, course);
      save();
      play('wrong');
    }
    advance(session, course);
    render();
    scrollTop();
  });
  root.querySelector('[data-act="next"]')?.addEventListener('click', () => {
    if (!course.nextCourseId || !canStartCourse(store, course.nextCourseId)) {
      play('lock');
      setToast('O próximo curso abre com o certificado.');
      return;
    }
    play('unlock');
    const next = courseById(course.nextCourseId);
    session = startSession(store, next);
    course = next;
    save();
    location.hash = `#/curso/${next.id}`;
    render();
    scrollTop();
  });
  root.querySelector('[data-act="download"]')?.addEventListener('click', async () => {
    const cert = store.records[course.id].certificate;
    if (!cert) return;
    try {
      const logoRes = await fetch(asset('brand/altec-logo.png'));
      const logoBytes = new Uint8Array(await logoRes.arrayBuffer());
      const bytes = await buildCertificatePdf({
        cert,
        course,
        logoBytes,
        trilha: store.trilhaCertificate,
      });
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `certificado-${course.id}.pdf`;
      a.click();
      play('drop');
    } catch (err) {
      setToast('Não deu para gerar o PDF. Tente de novo.');
    }
  });
  root.querySelectorAll('.tip').forEach((el) => {
    const open = () => {
      root.querySelectorAll('.tip.is-open').forEach((n) => n.classList.remove('is-open'));
      el.classList.add('is-open');
      placeBalloon(el);
      play('hover');
    };
    el.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      open();
    });
    el.addEventListener('mouseenter', open);
    el.addEventListener('focus', open);
    el.addEventListener('mouseleave', () => {
      if (document.activeElement !== el) closeBalloon();
    });
    el.addEventListener('blur', closeBalloon);
  });
  document.getElementById('tip-layer')?.addEventListener('click', closeBalloon);
}

function bootFromHash() {
  const m = location.hash.match(/^#\/curso\/([a-z-]+)/);
  if (m && store) {
    const id = m[1];
    if (canStartCourse(store, id)) {
      course = courseById(id);
      if (!session || session.courseId !== id) session = startSession(store, course);
    }
  }
}

export function mount(el) {
  root = el;
  restore();
  initSound(storage);
  bootFromHash();
  window.addEventListener('hashchange', () => {
    if (location.hash === '#/' || location.hash === '') {
      course = null;
      session = null;
    }
    render();
  });
  render();
}

export function _debug() {
  return { store, session, course, storageKey: STORAGE_KEY };
}
